import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { StepService } from 'services/professional/step/step.service';
import { filesExistAndValid, MessageQueryService } from '@certemy/ui-components';
import { UploadInfo } from 'utils/interfaces';
import { DynamicFormInfo } from 'interfaces/dynamic-form-info.interface';
import { VERIFICATION_TYPE } from 'constants/constants';
import { ComputedFrom } from '@certemy/common';
import { isPendingVerificationStatus, isStepSubmitted } from 'utils/step.helpers';
import { DynamicFormContentDetails } from './modal-content/dynamic-form-content-details/dynamic-form-content-details.component';
import _get from 'lodash-es/get';
import { MultipleDynamicFormContentComponent } from './modal-content/multiple-dynamic-form-content/multiple-dynamic-form-content.component';
import { DynamicFormInfoToSave, PAGES, SubmitFormInfo } from './dynamic-details.constants';
import { STEP_TYPE_STATUSES } from 'constants/status.constants';
import { DynamicForm, DynamicFormValue } from 'components/multiple-dynamic-form/multiple-dynamic-form.interface';
import { DynamicFormSection } from 'interfaces/dynamic-form-field.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'cm-dynamic-details',
  styleUrls: ['./dynamic-details.modal.component.scss'],
  templateUrl: './dynamic-details.modal.component.html',
})
export class DynamicDetailsComponent implements OnInit, OnDestroy {
  @Input() step;
  @Input() workflowTypeId: number;
  @Input() formPageFirst = false;
  @Output() updateStep = new EventEmitter();
  @Output() toggleMode: EventEmitter<string> = new EventEmitter();
  @ViewChild(MultipleDynamicFormContentComponent)
  multipleFormContent: MultipleDynamicFormContentComponent;
  PAGES = PAGES;
  private COMPACT_PAGES = [PAGES.EVIDENCE_CONFIRM, PAGES.FORM_CONFIRM, PAGES.RESET_CONFIRM];
  activePage: string;
  prevPages: string[] = [];
  uploadInfo: UploadInfo;
  files: File[] = [];
  formInfo: DynamicFormInfo = {
    fields: [],
    terms: '',
  };
  formData: DynamicFormValue[] = [];
  signature_link: string;
  sections: DynamicFormSection[];
  errorText: string;
  modalDetails: DynamicFormContentDetails;
  simulateMultipleForm = false;
  saveAsDraft = false;
  isSequentialFromStart = true;
  dynamicForms: DynamicForm[] = [];
  @ComputedFrom('step')
  get isStepSubmitted() {
    return isStepSubmitted(this.step.status_id);
  }

  @ComputedFrom('step')
  get allowMultipleEntries(): boolean {
    return this.step.content.allowMultipleEntries;
  }

  @ComputedFrom('step')
  get isPendingVerificationStatus() {
    return isPendingVerificationStatus(this.step.status_id);
  }

  @ComputedFrom('step', 'isStepSubmitted', 'isPendingVerificationStatus')
  get isSequentialView(): boolean {
    return this.step.content.isSequentialView && !this.isStepSubmitted && !this.isPendingVerificationStatus;
  }

  @ComputedFrom('step')
  get isDraft() {
    return (
      this.allowMultipleEntries && this.step.status_id === STEP_TYPE_STATUSES.INCOMPLETE && this.step.content.forms
    );
  }

  constructor(
    public activeModal: NgbActiveModal,
    private stepService: StepService,
    private messageQueryService: MessageQueryService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    if (this.isDraft) {
      this.activePage = this.PAGES.DRAFT;
      this.getDynamicFormData().subscribe((formInfo: DynamicFormInfo) => (this.formInfo = formInfo));
    } else if (this.formPageFirst) {
      this.openFormPage();
    } else {
      this.activePage = this.selectInitialPage();
    }

    this.modalDetails = {
      stepDescription: this.step.description,
      onBehalfIndicator: this.step.on_behalf_indicator,
    };
    if (this.isStepSubmitted || this.isDraft) {
      const { forms, documents, signature_link, terms, sections } = this.step.content;
      this.dynamicForms = forms || [];
      this.sections = sections;
      this.signature_link = signature_link;
      this.modalDetails.disclosureText = terms;

      if (_get(documents, 'length')) {
        this.files = documents.map((file) => ({ ...file, url: file.link }));
        this.goToPage(PAGES.EVIDENCE_COMPLETE);
      }
    }
  }

  private selectInitialPage() {
    if (this.isPendingVerificationStatus || this.isStepSubmitted) {
      return this.allowMultipleEntries ? this.PAGES.FORM_MULTIPLE_COMPLETE : this.PAGES.FORM_START;
    } else {
      return this.PAGES.MAIN_START;
    }
  }

  ngOnDestroy() {}

  openFormPage() {
    this.isSequentialFromStart = true;
    if (this.isStepSubmitted) {
      this.goToPage(this.allowMultipleEntries ? PAGES.FORM_MULTIPLE_COMPLETE : PAGES.FORM_START);
    } else {
      this.stepService
        .getDynamicFormData(this.step.step_id)
        .pipe(untilDestroyed(this))
        .subscribe((formInfo: DynamicFormInfo) => {
          this.dynamicForms = this.step.content.forms || [];
          this.sections = this.step.content.sections;
          this.formInfo = formInfo;
          const fields = _get(this.step, 'content.forms[0].fields', []);
          this.formInfo.fields = this.stepService.mapFormInfoFields(fields, this.formInfo);
          this.modalDetails.disclosureText = formInfo.terms;
          this.goToPage(this.allowMultipleEntries ? PAGES.FORM_MULTIPLE_COMPLETE : PAGES.FORM_START);
        });
    }
  }

  closeModal() {
    this.activeModal.close();
  }

  dismissModal() {
    this.activeModal.dismiss();
  }

  onFilesChange(files: File[]) {
    this.files = files;
  }

  confirmUpload() {
    if (!filesExistAndValid(this.files)) {
      this.errorText = 'Please add file(s) with appropriate file size';
      return;
    }

    this.errorText = '';

    this.uploadInfo = {
      textFields: [
        {
          label: 'Step name:',
          text: this.step.name,
        },
        {
          label: 'Step type:',
          text: this.step.step_type_name,
        },
      ],
      files: this.files,
    };

    this.goToPage(PAGES.EVIDENCE_CONFIRM);
  }

  uploadFiles() {
    const formData: FormData = new FormData();
    formData.append('id', this.step.step_instance_id);

    for (const file of this.files) {
      formData.append('file[]', file, file.name);
    }

    this.stepService.uploadDynamicStepEvidenceDocuments(formData).subscribe(() => this.completeStep());
  }

  onFormSubmit(formData: DynamicFormValue[]) {
    this.formData = Array.isArray(formData) ? formData : [formData];
    this.saveAsDraft = false;
    if (this.formInfo.signature_required) {
      this.goToPage(PAGES.SIGNATURE);
    } else {
      this.goToPage(PAGES.FORM_CONFIRM);
    }
  }

  onMultipleFormSubmit(submitInfo: SubmitFormInfo = { formData: [] }) {
    this.saveAsDraft = submitInfo.saveAsDraft;
    if (submitInfo.saveAsDraft) {
      this.saveFormAsDraft(submitInfo.formData);
    } else {
      this.onFormSubmit(submitInfo.formData);
    }
  }

  saveMultipleFormFromDraft() {
    this.simulateMultipleForm = true;
    this.changeDetector.detectChanges();
    const data = this.multipleFormContent.validateAndGetMultipleFormData();
    if (!_get(data, 'length')) {
      this.onScreenChange(PAGES.FORM_MULTIPLE_COMPLETE);
    } else {
      this.onFormSubmit(data);
    }
    this.simulateMultipleForm = false;
  }

  private saveFormAsDraft(formData) {
    this.formData = formData;
    this.submitDynamicForm().subscribe(() => this.goToPage(PAGES.DRAFT));
  }

  private getDynamicFormData() {
    return this.stepService.getDynamicFormData(this.step.step_id).pipe(untilDestroyed(this));
  }

  onSignatureSubmit(signature) {
    this.signature_link = signature;
    this.goToPage(PAGES.FORM_CONFIRM);
  }

  onFormConfirm() {
    this.submitDynamicForm().subscribe(() => this.completeStep());
  }

  private submitDynamicForm() {
    const dataToSend: DynamicFormInfoToSave = {
      saveAsDraft: this.saveAsDraft,
      forms: this.formData,
      id: this.step.step_instance_id,
      signature_link: this.signature_link,
    };

    return this.stepService.submitDynamicForm(dataToSend).pipe(untilDestroyed(this));
  }

  onResetConfirm() {
    this.stepService.resetStep(this.step.step_instance_id).subscribe(() => {
      this.updateStep.emit();
      this.files = [];
      this.messageQueryService.addMessage('Step has been successfully reset');
      this.goToPage(this.PAGES.MAIN_START);
    });
  }

  private completeStep() {
    const messageText = [
      VERIFICATION_TYPE.VERIFICATION_REQUIRED,
      VERIFICATION_TYPE.DOUBLE_VERIFICATION_REQUIRED,
    ].includes(this.step.verificationType)
      ? 'Step has been submitted for review'
      : 'Step has been successfully completed';
    this.messageQueryService.addMessage(messageText);
    this.closeModal();
  }

  goToPage(page: string): void {
    if (this.isToggleRequired(this.activePage, page)) {
      this.toggleMode.emit();
    }

    this.prevPages.push(this.activePage);
    this.activePage = page;
  }

  backToPreviousPage() {
    const nextPage = this.prevPages.pop();
    this.isSequentialFromStart = nextPage !== PAGES.FORM_START && this.isSequentialView;
    if (this.isToggleRequired(this.activePage, nextPage)) {
      this.toggleMode.emit();
    }

    this.activePage = nextPage;
  }

  private isToggleRequired(currentPage: string, nextPage: string): boolean {
    return this.COMPACT_PAGES.includes(currentPage) !== this.COMPACT_PAGES.includes(nextPage);
  }

  onScreenChange(page: string) {
    if (page === PAGES.BACK) {
      this.backToPreviousPage();
    } else {
      this.goToPage(page);
    }
  }
}
